import type {
  BusinessRole,
  CharityRole
} from '@/types/roles';
import type {
  Volunteer,
  CharityDashboardStats
} from '@/types/charity';
import type {
  Product,
  Order,
  Business,
  BusinessStaff,
  BusinessDashboardStats,
  BusinessStatistics
} from '@/types/business';
import { createClient } from './supabase';
// Import services instead of implementing duplicate functionality
import { authServiceV2 } from './auth.service';
import { eventsService } from './events.service';
import { DonationsService } from './donations.service';
import { httpService } from './http.service';

// Error handler function
const handleError = (error: any) => {
  console.error('API Error:', error);
  throw error;
};

// Re-export auth for backward compatibility
export const auth = authServiceV2;

// Business services
export const business = {
  async getProfile(): Promise<Business> {
    const supabase = await createClient();
    
    // First get the user's profile to find their business_id
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('business_id')
      .single();
    
    if (profileError) throw profileError;
    
    // Then get the business details
    const { data, error } = await supabase
      .from('businesses')
      .select('*')
      .eq('id', profile.business_id)
      .single();
    
    if (error) throw error;
    return data as Business;
  },

  async update(id: string, data: Partial<Business>): Promise<Business> {
    const supabase = await createClient();
    const { data: updatedBusiness, error } = await supabase
      .from('businesses')
      .update(data)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return updatedBusiness as Business;
  },

  async getStaff(businessId: string): Promise<BusinessStaff[]> {
    const supabase = await createClient();
    const { data, error } = await supabase
      .from('business_staff')
      .select(`
        id,
        role,
        user_id,
        business_id,
        profiles:user_id (
          name,
          email
        )
      `)
      .eq('business_id', businessId);
    
    if (error) throw error;
    return data as unknown as BusinessStaff[];
  },

  async addStaff(staffData: Partial<BusinessStaff>): Promise<BusinessStaff> {
    const supabase = await createClient();
    const { data, error } = await supabase
      .from('business_staff')
      .insert(staffData)
      .select()
      .single();
    
    if (error) throw error;
    return data as BusinessStaff;
  },

  async updateStaff(id: string, data: Partial<BusinessStaff>): Promise<BusinessStaff> {
    const supabase = await createClient();
    const { data: updatedStaff, error } = await supabase
      .from('business_staff')
      .update(data)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return updatedStaff as BusinessStaff;
  },

  async removeStaff(id: string): Promise<void> {
    const supabase = await createClient();
    const { error } = await supabase
      .from('business_staff')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  products: {
    async getAll(businessId: string): Promise<Product[]> {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('business_id', businessId);
      
      if (error) throw error;
      return data as Product[];
    },

    async get(id: string): Promise<Product> {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('product_id', id)
        .single();
      
      if (error) throw error;
      return data as Product;
    },

    async create(product: Partial<Product>): Promise<Product> {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('products')
        .insert(product)
        .select()
        .single();
      
      if (error) throw error;
      return data as Product;
    },

    async update(id: string, product: Partial<Product>): Promise<Product> {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('products')
        .update(product)
        .eq('product_id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Product;
    },

    async delete(id: string): Promise<void> {
      const supabase = await createClient();
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('product_id', id);
      
      if (error) throw error;
    }
  },

  orders: {
    async getAll(businessId: string): Promise<Order[]> {
      const supabase = await createClient();
      
      // First get all products for this business
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('product_id')
        .eq('business_id', businessId);
      
      if (productsError) throw productsError;
      
      // Then get all orders containing these products
      // Note: This is a simplification. In reality, you'd need a junction table or orders.items to be a JSONB array
      const productIds = products.map(p => p.product_id);
      
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .in('product_id', productIds);
      
      if (error) throw error;
      return data as Order[];
    },

    async get(id: string): Promise<Order> {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('order_id', id)
        .single();
      
      if (error) throw error;
      return data as Order;
    },

    async update(id: string, orderData: Partial<Order>): Promise<Order> {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('orders')
        .update(orderData)
        .eq('order_id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Order;
    }
  },

  statistics: {
    async getDashboardStats(businessId: string): Promise<BusinessDashboardStats> {
      const supabase = await createClient();
      
      // Get order count (simplified)
      const { count: orderCount, error: orderError } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', businessId);
      
      if (orderError) throw orderError;
      
      // Note: We're no longer using ratings in the dashboard stats
    // This was previously used to calculate avgRating, but that property
    // doesn't exist in the BusinessDashboardStats interface
    // If needed, this could be added to a different property or fetched separately
      
      // Return object that matches BusinessDashboardStats interface
      return {
        // Note: productCount is now accessed via statistics.getProductCount()
        total_orders: orderCount || 0,
        // Store average rating in a property that exists in the interface
        totalOrders: orderCount || 0,  // Add camelCase version as well
        recent_orders: [], // Would need additional query
        // Other required fields with default values
        total_revenue: 0,
        total_savings_provided: 0,
        active_products: 0,
        pending_orders: 0
      };
    },

    /**
     * Get detailed business statistics
     * Note: For product count, use getProductCount() instead
     */
    async getDetailedStats(): Promise<BusinessStatistics> {
      // In a real implementation, we would use the businessId and supabase client
      // to fetch detailed statistics from the database
      
      // This would involve multiple complex queries to gather all statistics
      // Simplified implementation for now
      // Product counts should be retrieved using getProductCount() separately
      return {
        daily_stats: [],
        monthly_stats: [],
        product_performance: [],
        charity_interactions: [],
        impact_metrics: {
          total_charities_helped: 0,
          total_food_saved: 0,
          total_environmental_impact: '0 kg CO2 saved',
          total_community_impact: '0 meals provided'
        }
      };
    }
  }
};

// Charity services
export const charity = {
  async getProfile(): Promise<Business> {
    const supabase = await createClient();
    
    // First get the user's profile to find their business_id
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('business_id')
      .single();
    
    if (profileError) throw profileError;
    
    // Then get the charity details (which is a business with type='charity')
    const { data, error } = await supabase
      .from('businesses')
      .select('*')
      .eq('id', profile.business_id)
      .eq('business_type', 'charity')
      .single();
    
    if (error) throw error;
    return data as Business;
  },

  async update(id: string, data: Partial<Business>): Promise<Business> {
    const supabase = await createClient();
    const { data: updatedCharity, error } = await supabase
      .from('businesses')
      .update(data)
      .eq('id', id)
      .eq('business_type', 'charity')
      .select()
      .single();
    
    if (error) throw error;
    return updatedCharity as Business;
  }
};

// Events services - use the dedicated events.service.ts
export const events = eventsService;

// Donations services - use the dedicated donations.service.ts
export const donations = DonationsService;

// Volunteers services - volunteers are associated with businesses of type='charity'
export const volunteers = {
  async getAll(businessId?: string): Promise<Volunteer[]> {
    try {
      const supabase = await createClient();
      let query = supabase.from('volunteers').select(`
        *,
        profiles:user_id (
          name,
          email
        ),
        events:event_id (
          event_name,
          event_date
        )
      `);
      
      if (businessId) {
        query = query.eq('business_id', businessId);
      } else {
        // Get the user's business_id
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('business_id')
          .single();
        
        if (profileError) throw profileError;
        query = query.eq('business_id', profileData.business_id);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as unknown as Volunteer[];
    } catch (error) {
      return handleError(error);
    }
  },

  async get(id: string): Promise<Volunteer> {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('volunteers')
        .select(`
          *,
          profiles:user_id (
            name,
            email
          ),
          events:event_id (
            event_name,
            event_date
          )
        `)
        .eq('volunteer_id', id)
        .single();
      
      if (error) throw error;
      return data as unknown as Volunteer;
    } catch (error) {
      return handleError(error);
    }
  },

  async create(volunteer: Partial<Volunteer>): Promise<Volunteer> {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('volunteers')
        .insert(volunteer)
        .select()
        .single();
      
      if (error) throw error;
      return data as Volunteer;
    } catch (error) {
      return handleError(error);
    }
  },

  async update(id: string, volunteer: Partial<Volunteer>): Promise<Volunteer> {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('volunteers')
        .update(volunteer)
        .eq('volunteer_id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Volunteer;
    } catch (error) {
      return handleError(error);
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const supabase = await createClient();
      const { error } = await supabase
        .from('volunteers')
        .delete()
        .eq('volunteer_id', id);
      
      if (error) throw error;
    } catch (error) {
      return handleError(error);
    }
  },

  async approve(id: string): Promise<Volunteer> {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('volunteers')
        .update({ is_approved: true })
        .eq('volunteer_id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Volunteer;
    } catch (error) {
      return handleError(error);
    }
  }
};

// Business statistics
// Statistics services - for both business and charity dashboards
export const statistics = {
  /**
   * Get product count for a business
   * This function is used by product-related pages rather than storing count in businessStatistics
   */
  async getProductCount(businessId?: string): Promise<number> {
    try {
      const supabase = await createClient();
      
      // If no businessId is provided, get it from the user's profile
      if (!businessId) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('business_id')
          .single();
        
        if (profileError) throw profileError;
        businessId = profileData.business_id;
      }
      
      const { count, error } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', businessId);
      
      if (error) throw error;
      
      return count || 0;
    } catch (error) {
      return handleError(error);
    }
  },
  async getDashboardStats(): Promise<BusinessDashboardStats | CharityDashboardStats> {
    try {
      const supabase = await createClient();
      
      // First get the user's profile to find their business_id and type
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(`
          business_id,
          businesses:business_id (business_type)
        `)
        .single();
      
      if (profileError) throw profileError;
      
      const businessId = profileData.business_id;
      // Fix the type issue by using proper type assertion
      const businessType = profileData.businesses ? (profileData.businesses as any).business_type : null;
      
      // If business type is charity, return charity dashboard stats
      if (businessType === 'charity') {
        // Get donation count
        const { count: donationCount, error: donationError } = await supabase
          .from('donations_for_charity')
          .select('*', { count: 'exact', head: true })
          .eq('charity_id', businessId);
        
        if (donationError) throw donationError;
        
        // Get event count
        const { count: eventCount, error: eventError } = await supabase
          .from('events')
          .select('*', { count: 'exact', head: true })
          .eq('created_by', businessId);
        
        if (eventError) throw eventError;
        
        // Get volunteer count
        const { count: volunteerCount, error: volunteerError } = await supabase
          .from('volunteers')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', businessId);
        
        if (volunteerError) throw volunteerError;
        
        // Create charity dashboard stats object with the correct structure
        return {
          donations: {
            total: donationCount || 0,
            pending: 0,
            value_this_month: 0,
            percent_change: 0
          },
          events: {
            total: eventCount || 0,
            upcoming: 0,
            attendees_this_month: 0,
            percent_change: 0
          },
          volunteers: {
            total: volunteerCount || 0,
            active: 0,
            hours_this_month: 0,
            percent_change: 0
          },
          impact: {
            meals_provided: 0,
            people_served: 0,
            food_waste_reduced: 0
          },
          recent_activity: []
        } as CharityDashboardStats;
      } 
      // Otherwise return business dashboard stats
      else {
        // Get order count
        const { count: orderCount, error: orderError } = await supabase
          .from('orders')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', businessId);
        
        if (orderError) throw orderError;
        
        // Get ratings
        const { data: ratings, error: ratingsError } = await supabase
          .from('order_ratings')
          .select('rating')
          .eq('business_id', businessId);
        
        if (ratingsError) throw ratingsError;
        
        const avgRating = ratings && ratings.length > 0 
          ? ratings.reduce((sum, item) => sum + item.rating, 0) / ratings.length 
          : 0;
        
        // Create business dashboard stats with the correct structure
        return {
          // Use properties that match BusinessDashboardStats interface
          total_orders: orderCount || 0,
          avgRating,
          recent_orders: [], // Would need additional query
          // Add other required properties
          total_revenue: 0,
          total_savings_provided: 0,
          active_products: 0,
          pending_orders: 0
        } as BusinessDashboardStats;
      }
    } catch (error) {
      return handleError(error);
    }
  }
};

// Roles endpoints
export const roles = {
  async getBusinessRoles(): Promise<BusinessRole[]> {
    // These are static roles, no need to query the database
    return ['owner', 'manager', 'staff'];
  },

  async getCharityRoles(): Promise<CharityRole[]> {
    // These are static roles, no need to query the database
    return ['owner', 'manager', 'supervisor', 'staff', 'volunteer'];
  }
};

// Upload endpoints - for business pictures
export const upload = {
  async image(file: File): Promise<{ url: string }> {
    try {
      const supabase = await createClient();
      
      // Generate a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      
      // Upload to the businesspictures bucket
      const { data, error } = await supabase
        .storage
        .from('businesspictures')
        .upload(fileName, file);
      
      if (error) throw error;
      
      // Get the public URL
      const { data: publicUrlData } = supabase
        .storage
        .from('businesspictures')
        .getPublicUrl(data.path);
      
      return { url: publicUrlData.publicUrl };
    } catch (error) {
      return handleError(error);
    }
  }
};

// Analytics endpoints
export const analytics = {
  async getDonationStats(startDate: string, endDate: string): Promise<any> {
    try {
      const supabase = await createClient();
      
      // First get the user's profile to find their business_id
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(`
          business_id,
          businesses:business_id (business_type)
        `)
        .single();
      
      if (profileError) throw profileError;
      
      // Determine if user is a charity or business
      const isCharity = profileData.businesses ? (profileData.businesses as any).business_type === 'charity' : false;
      
      // Then get the donation stats based on user type
      const { data, error } = await supabase
        .from('donations_for_charity')
        .select('*')
        .eq(isCharity ? 'charity_id' : 'business_id', profileData.business_id)
        .gte('created_at', startDate)
        .lte('created_at', endDate);
      
      if (error) throw error;
      
      // Process the data to generate statistics
      return {
        totalDonations: data?.length || 0,
        donationsByDate: {},
        donationsByCategory: {}
      };
    } catch (error) {
      return handleError(error);
    }
  },
  
  async getVolunteerStats(startDate: string, endDate: string): Promise<any> {
    try {
      const supabase = await createClient();
      
      // First get the user's profile to find their business_id
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('business_id')
        .single();
      
      if (profileError) throw profileError;
      
      // Then get the volunteer stats
      const { data, error } = await supabase
        .from('volunteers')
        .select('*')
        .eq('business_id', profileData.business_id)
        .gte('created_at', startDate)
        .lte('created_at', endDate);
      
      if (error) throw error;
      
      // Process the data to generate statistics
      return {
        totalVolunteers: data?.length || 0,
        volunteersByDate: {},
        volunteersByRole: {}
      };
    } catch (error) {
      return handleError(error);
    }
  }
};

// Export all API services
export const api = {
  auth,
  business,
  charity,
  events,
  donations,
  volunteers,
  roles,
  upload,
  statistics,
  analytics,
  // Add HTTP methods
  get: httpService.get.bind(httpService),
  post: httpService.post.bind(httpService),
  patch: httpService.patch.bind(httpService),
  put: httpService.patch.bind(httpService), // Add put method as an alias for patch
  delete: httpService.delete.bind(httpService),
  // Add defaults property for setting headers
  defaults: {
    headers: {
      common: {} as Record<string, string>
    }
  }
};

// Note: We've refactored this file to use dedicated service modules:
// - auth is now imported from auth.service.ts
// - events is now imported from events.service.ts
// - donations is now imported from donations.service.ts
// This improves code organization and maintainability while maintaining backward compatibility.

export default api;